import feather from "@/assets/svg/feather.svg";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFeature } from "@/hooks/use-feature";
import { authService, useUser } from "@/services/auth-service";
import {
  BookCopy,
  Crown,
  Library,
  LogIn,
  LogOut,
  Menu,
  PenTool,
  Settings,
  User,
  UserPlus,
} from "lucide-react";
import { Link, To } from "react-router-dom";
import { Badge } from "./ui/badge";

interface UpgradeButtonProps {
  variant?: "default" | "outline" | "ghost";
  className?: string;
  mobile?: boolean;
}

const UpgradeButton = ({
  variant = "outline",
  className = "",
  mobile = false,
}: UpgradeButtonProps) => {
  const { limits } = useFeature();

  if (!limits || limits?.plan !== "FREE") return null;

  return (
    <Link to="/plans">
      <Button
        variant={variant}
        className={`flex items-center gap-2 bg-primary/10 border-primary/20 ${className}`}
      >
        <Crown className="w-4 h-4 text-primary" />
        {!mobile && (
          <>
            <span>Upgrade</span>
            <Badge
              variant="secondary"
              className="ml-2 bg-primary/20 text-primary hover:bg-primary/30"
            >
              Pro
            </Badge>
          </>
        )}
      </Button>
    </Link>
  );
};

const Header = () => {
  let isAuthenticated = false;
  const user = useUser();

  if (user?.email) {
    isAuthenticated = true;
  }

  interface UserNavigationItem {
    name: string;
    href?: To;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    icon?: any;
    onClick?: () => void;
  }

  let navigation: UserNavigationItem[] = [
    { name: "Library", href: "/library", icon: BookCopy },
    { name: "Create Story", href: "/create", icon: PenTool },
  ];

  if (!isAuthenticated) {
    navigation = [
      { name: "Plans", href: "/plans", icon: Crown },
      { name: "Examples", href: "/examples", icon: BookCopy },
    ];
  }

  const userNavigation: UserNavigationItem[] = [
    { name: "Examples", href: "/examples", icon: Library },
    { name: "Settings", href: "/settings", icon: Settings },
    { name: "Sign out", onClick: () => authService.logout(), icon: LogOut },
  ];

  return (
    <header className="sticky top-0 z-10 shadow bg-slate-50 dark:bg-slate-950">
      <nav className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center font-bold text-gray-900 dark:text-sky-50 font-magical"
            >
              <div className="relative flex items-center">
                <img
                  src={feather}
                  alt="WhimsyQuill Logo"
                  className="absolute w-12 h-12 -rotate-45 -left-1"
                />
                <span className="flex items-baseline ml-8">
                  <span className="md:text-[40px] text-[35px]">W</span>
                  <span className="md:text-[23px] text-[20px]">himsy</span>
                  <span className="md:text-[40px] text-[35px] md:ml-[-20px] ml-[-18px]">
                    Q
                  </span>
                  <span className="md:text-[23px] text-[20px]">uill</span>
                </span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            {navigation.map((item, i) => (
              <div
                key={i}
                className="relative inline-flex items-center space-x-0 transition-colors group hover:text-primary dark:hover:text-primary"
              >
                <item.icon className="w-5 h-5" />
                <Link
                  key={item.name}
                  to={item.href}
                  className="px-2 py-1 font-bold"
                >
                  {item.name}
                </Link>
                <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary transform scale-x-0 transition-transform group-hover:scale-x-100" />
              </div>
            ))}

            <UpgradeButton />
            <ThemeToggle />

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" aria-label="Open user menu">
                    <User color="#fff" className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback>
                        {user?.email?.[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm truncate">{user?.email}</span>
                  </div>
                  <DropdownMenuSeparator />
                  {userNavigation.map((item) => (
                    <DropdownMenuItem key={item.name}>
                      {item.href ? (
                        <Link
                          to={item.href}
                          className="flex items-center w-full gap-2"
                        >
                          <item.icon className="w-4 h-4" />
                          {item.name}
                        </Link>
                      ) : (
                        <button
                          onClick={item.onClick}
                          className="flex items-center w-full gap-2 text-left"
                        >
                          <item.icon className="w-4 h-4" />
                          {item.name}
                        </button>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/signin">
                  <Button>Sign In</Button>
                </Link>
                <Link to="/signup">
                  <Button>Sign Up</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Navigation */}
          <div className="flex items-center space-x-2 md:hidden">
            <ThemeToggle />
            <UpgradeButton mobile={true} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" aria-label="Open navigation menu">
                  <Menu color="#fff" className="w-6 h-6" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {isAuthenticated ? (
                  <>
                    <div className="flex items-center gap-2 px-2 py-1.5">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback>
                          {user?.email?.[0].toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm truncate">{user?.email}</span>
                    </div>
                    <DropdownMenuSeparator className="my-2" />
                  </>
                ) : (
                  <></>
                )}
                {navigation.map((item) => (
                  <DropdownMenuItem key={item.name}>
                    <Link
                      to={item.href}
                      className="flex items-center w-full gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    >
                      <item.icon className="w-4 h-4" />
                      {item.name}
                    </Link>
                  </DropdownMenuItem>
                ))}

                <DropdownMenuSeparator className="my-2" />

                {isAuthenticated ? (
                  <>
                    {userNavigation.map((item) => (
                      <DropdownMenuItem key={item.name}>
                        {item.href ? (
                          <Link
                            to={item.href}
                            className="flex items-center w-full gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                          >
                            <item.icon className="w-4 h-4" />
                            {item.name}
                          </Link>
                        ) : (
                          <button
                            onClick={item.onClick}
                            className="flex items-center w-full gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                          >
                            <item.icon className="w-4 h-4" />
                            {item.name}
                          </button>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </>
                ) : (
                  <>
                    <DropdownMenuItem>
                      <Link
                        to="/signin"
                        className="flex items-center w-full gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                      >
                        <LogIn className="w-4 h-4" />
                        Sign In
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Link
                        to="/signup"
                        className="flex items-center w-full gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                      >
                        <UserPlus className="w-4 h-4" />
                        Sign Up
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
